import React, { useRef, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import RMSDataGridRow from './RMSDataGridRow';
import clsx from 'clsx';

const RMSDataGridContent = ({
  primaryKey,
  columns,
  rows = [],
  enableRowReorder = false,
  onRowsChange,
  renderActions,
  sortField,
  sortDirection = 'asc',
  onSort,
  enableColumnResize = false,
  enableColumnReorder = false,
  onColumnResize,
  onColumnReorder,
  onRowClick,
}) => {
  const dragFieldRef = useRef(null);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    if (result.source.index === result.destination.index) return;

    const reorderedRows = [...rows];

    const [removed] = reorderedRows.splice(result.source.index, 1);

    reorderedRows.splice(result.destination.index, 0, removed);

    onRowsChange?.(reorderedRows);
  };

  const handleResizeMouseDown = useCallback(
    (field) => (event) => {
      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startWidth = event.currentTarget.parentElement.getBoundingClientRect().width;

      const handleMouseMove = (moveEvent) => {
        const newWidth = Math.max(60, startWidth + (moveEvent.clientX - startX));
        onColumnResize?.(field, newWidth);
      };

      const handleMouseUp = () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [onColumnResize]
  );

  const handleHeaderDragStart = useCallback(
    (field) => (event) => {
      event.dataTransfer.effectAllowed = 'move';
      dragFieldRef.current = field;
    },
    []
  );

  const handleHeaderDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleHeaderDrop = useCallback(
    (targetField) => (event) => {
      event.preventDefault();
      const sourceField = dragFieldRef.current;
      dragFieldRef.current = null;

      if (!sourceField || sourceField === targetField) return;

      onColumnReorder?.(sourceField, targetField);
    },
    [onColumnReorder]
  );

  const renderRows = () =>
    rows?.map((row, index) => {
      if (!enableRowReorder) {
        return (
          <RMSDataGridRow
            key={row[primaryKey] || index}
            row={row}
            columns={columns}
            renderActions={renderActions}
            onRowClick={onRowClick}
          />
        );
      }

      return (
        <Draggable
          key={row[primaryKey] || index}
          draggableId={String(row[primaryKey] || index)}
          index={index}>
          {(provided) => (
            <RMSDataGridRow
              row={row}
              columns={columns}
              enableRowReorder={enableRowReorder}
              innerRef={provided.innerRef}
              draggableProps={provided.draggableProps}
              dragHandleProps={provided.dragHandleProps}
              renderActions={renderActions}
              onRowClick={onRowClick}
            />
          )}
        </Draggable>
      );
    });

  const renderHeaderCell = (column) => {
    const label = onSort ? (
      <TableSortLabel
        active={sortField === column.field}
        direction={sortField === column.field ? sortDirection : 'asc'}
        onClick={() => onSort(column.field)}>
        {column.title}
      </TableSortLabel>
    ) : (
      column.title
    );

    return (
      <TableCell
        key={column.field}
        className={enableColumnResize ? 'rms-data-grid__header-cell' : undefined}
        style={column.width ? { width: column.width } : undefined}
        draggable={enableColumnReorder}
        onDragStart={enableColumnReorder ? handleHeaderDragStart(column.field) : undefined}
        onDragOver={enableColumnReorder ? handleHeaderDragOver : undefined}
        onDrop={enableColumnReorder ? handleHeaderDrop(column.field) : undefined}>
        {label}

        {enableColumnResize && (
          <span
            className="rms-data-grid__resize-handle"
            onMouseDown={handleResizeMouseDown(column.field)}
          />
        )}
      </TableCell>
    );
  };

  return (
    <TableContainer className="evidence-table-responsive">
      <Table
        stickyHeader
        size="small"
        className={clsx('rms-data-grid', {
          'rms-data-grid--resizable': enableColumnResize,
        })}>
        <TableHead>
          <TableRow>
            {enableRowReorder && <TableCell width={40} />}

            {columns.map((column) => renderHeaderCell(column))}

            {renderActions && <TableCell align="center">Actions</TableCell>}
          </TableRow>
        </TableHead>

        {!enableRowReorder ? (
          <TableBody>{renderRows()}</TableBody>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="table-body">
              {(provided) => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {renderRows()}

                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Table>
    </TableContainer>
  );
};

export default RMSDataGridContent;
